import './AccordionSteps.css';
import React, { useCallback } from 'react';
import { get } from '../../utils';
import { RichText, Text, Link } from '@sitecore-jss/sitecore-jss-react';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import IconPlus from '../atoms/Icons/IconPlus';
import IconMinus from '../atoms/Icons/IconMinus';
import { ReactComponent as IconChevronDown } from '../../resources/icons/arrowDownC.svg';
import { ReactComponent as IconExpandPlus } from '../../resources/icons/black-plus.svg';
import { ReactComponent as IconChevronRightDetLight } from '../../resources/icons/chevron-right-24-px.svg';
import { ReactComponent as IconMinuss } from '../../resources/icons/accordion-minus.svg';
import RouterLink from '../atoms/RouterLink';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { convertToAnalyticObject } from '../../utils/analytics';
export const AccordionSteps = _ref => {
  let {
    fields,
    params
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const oneColumn = params !== undefined && params['OneColumn'] === '1' ? true : false;
  const [activeItemsIds, setActiveItemsIds] = React.useState([]);
  const isDetSite = get(params, 'RenderingStyle') === 'DDA';
  const hasTitle = Boolean(get(fields, 'data.item.title.jss.value'));
  const description = get(fields, 'data.item.description.jss.value', '');
  const hasItems = Boolean(get(fields, 'data.item.children.length'));
  const items = hasItems ? fields.data.item.children : [];
  const filteredItems = !pageEditing ? items.filter(i => get(i, 'title.jss.value') && get(i, 'description.jss.value')) : items;
  const readAllLabel = get(fields, 'data.item.readAllLabel.field.value');
  const href = get(fields, 'data.item.readAllLink.field.value.href', '/');
  const readAllLinkHref = href.endsWith('pdf') ? `${sitecoreContext.origin}/${href}` : href;
  const ctaLeftIcon = get(fields, 'data.item.ctaLeftIcon.targetItem');
  const ctaRightIcon = get(fields, 'data.item.ctaRightIcon.targetItem');
  const middleArrIndex = hasItems ? Math.ceil(filteredItems.length / 2) : 0;
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const expandAllText = get(fields, 'data.item.expandAllText.jss.value');
  const collapseAllText = get(fields, 'data.item.collapseAllText.jss.value');
  const allItemsExpanded = pageEditing || filteredItems.length === activeItemsIds.length;
  const showStepNumbers = Boolean(get(fields, 'data.item.showStepNumbers.jss.value'));
  React.useEffect(() => {
    const isFirstExpanded = Boolean(get(fields, 'data.item.isFirstExpanded.jss.value'));

    // expanding all faq items if in page editing mode
    if (pageEditing) {
      setActiveItemsIds(filteredItems.map(i => i.id));
    } else if (isFirstExpanded && hasItems) setActiveItemsIds([filteredItems[0].id]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccordionItemClick = React.useCallback((e, id, isActive) => {
    e.preventDefault();
    const nextActiveItemsIds = isActive ? activeItemsIds.filter(i => i !== id) : [...activeItemsIds, id];
    setActiveItemsIds(nextActiveItemsIds);
  }, [activeItemsIds]);
  const handleToggleAllItemsClick = useCallback(e => {
    e.preventDefault();
    const nextActiveItemsIds = allItemsExpanded ? [] : filteredItems.map(item => item.id);
    setActiveItemsIds(nextActiveItemsIds);
  }, [allItemsExpanded, filteredItems]);
  if (!fields) {
    console.warn('AccordionSteps component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", {
        className: "no-data"
      }, "[AccordionSteps component is empty: please provide data source]");
    }
    return null;
  }
  const renderItem = (item, number, pageEditing) => {
    const isActive = activeItemsIds.includes(item.id);
    const ctaLabel = get(item, 'ctaLabel.jss.value');
    const ctaLink = get(item, 'ctaLink.jss.value.href');
    return /*#__PURE__*/React.createElement("div", {
      className: `accordion__list ${isActive ? "accordion__list--active" : ""}`,
      key: item.id,
      "data-cy": "accordion-steps-single-step"
    }, /*#__PURE__*/React.createElement("div", {
      className: "accordion__heading body-medium",
      onClick: e => {
        if (!pageEditing) handleAccordionItemClick(e, item.id, isActive);
      },
      onKeyPress: e => {
        if (e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          handleAccordionItemClick(e, item.id, isActive);
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "accordion__anchor-wrapper"
    }, showStepNumbers && /*#__PURE__*/React.createElement("div", {
      "data-cy": "accordion-steps-step-number"
    }, number < 10 ? '0' : '', number, "."), /*#__PURE__*/React.createElement(Text, {
      tag: "a",
      className: "accordion__anchor",
      href: "/",
      field: item.title.jss,
      "data-cy": "accordion-steps-anchor-title"
    })), !pageEditing && !isDetSite && /*#__PURE__*/React.createElement("span", {
      className: "accordion__icon"
    }, isActive ? /*#__PURE__*/React.createElement(IconMinus, null) : /*#__PURE__*/React.createElement(IconPlus, null)), !pageEditing && isDetSite && /*#__PURE__*/React.createElement("span", {
      className: "accordion__icon"
    }, /*#__PURE__*/React.createElement(IconChevronDown, {
      className: `${isActive ? "iconDown" : "iconUp"}`
    }))), isActive && /*#__PURE__*/React.createElement("div", {
      className: "accordion__content"
    }, /*#__PURE__*/React.createElement("div", {
      className: "body-small accordion__p"
    }, /*#__PURE__*/React.createElement(RichText, {
      tag: "div",
      field: item.description.jss,
      "data-cy": "accordion-steps-anchor-content"
    })), pageEditing ? /*#__PURE__*/React.createElement(Link, {
      className: "default-link",
      field: item.ctaLink.jss
    }, /*#__PURE__*/React.createElement(Text, {
      tag: "span",
      className: "accordionStepCtaText",
      field: item.ctaLabel.jss
    }), /*#__PURE__*/React.createElement(IconChevronRightDetLight, {
      className: "iconRight"
    })) : ctaLabel && ctaLink && /*#__PURE__*/React.createElement("a", {
      className: "default-link accordion__cta-link",
      "aria-label": "Icon Title",
      href: item.ctaLink.jss.value.href
    }, /*#__PURE__*/React.createElement(Text, {
      tag: "span",
      className: "accordionStepCtaText",
      field: item.ctaLabel.jss
    }), /*#__PURE__*/React.createElement(IconChevronRightDetLight, {
      className: "iconRight"
    }))));
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    anchorUrlParameter: params['Anchor Url Parameter'],
    isDetSite: isDetSite
  }, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json",
    dangerouslySetInnerHTML: {
      __html: `
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [

            ${items.map(item => {
        return `{
                "@type": "Question",gD	go to declaration
                "name": ${JSON.stringify(get(item, 'title.jss.value'))},
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": ${JSON.stringify(get(item, 'description.jss.value'))}
                }
              }`;
      })}]
          }`
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "row m01-accordion-steps"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-md-12"
  }, /*#__PURE__*/React.createElement("div", {
    className: "section-steps",
    "data-cy": "accordion-steps"
  }, hasTitle && /*#__PURE__*/React.createElement("div", {
    className: "accordion-main-heading heading-w-link"
  }, /*#__PURE__*/React.createElement("div", {
    className: "titleDescriptionWrapper"
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "div",
    className: "step-title",
    field: fields.data.item.title.jss,
    "data-cy": "accordion-steps-title"
  }), description ? /*#__PURE__*/React.createElement(RichText, {
    className: "accordionDescription",
    tag: "div",
    field: {
      value: description
    }
  }) : null), !pageEditing && collapseAllText && expandAllText && /*#__PURE__*/React.createElement("div", {
    className: "expand-wrapper"
  }, /*#__PURE__*/React.createElement("a", {
    className: "expand-accordion-btn default-link",
    href: "/",
    onClick: handleToggleAllItemsClick,
    "data-cy": "accordion-steps-expand-all"
  }, allItemsExpanded ? collapseAllText : expandAllText), isDetSite ? /*#__PURE__*/React.createElement("div", {
    className: "iconBlock"
  }, allItemsExpanded ? /*#__PURE__*/React.createElement(IconMinuss, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(IconExpandPlus, {
    className: "icon"
  })) : null)), /*#__PURE__*/React.createElement("div", {
    className: `accordion ${showStepNumbers ? 'accordion--steps' : 'accordion--faq'} ${oneColumn ? 'accordion--one-column' : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "accordion__col accordion__col--left"
  }, filteredItems.map((item, index) => {
    if (index < middleArrIndex) return renderItem(item, index + 1, pageEditing);
    return null;
  })), /*#__PURE__*/React.createElement("div", {
    className: "accordion__col accordion__col--right"
  }, filteredItems.map((item, index) => {
    if (index >= middleArrIndex) return renderItem(item, index + 1, pageEditing);
    return null;
  }))), isDetSite && readAllLabel && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(RouterLink, {
    className: "btn btn-primary accordion__readmore",
    href: readAllLinkHref,
    onClick: () => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['readAllAnalytics'], params)),
    title: readAllLabel
  }, ctaLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: ctaLeftIcon.svg.jss.value
    }
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "btn-link__text"
  }, readAllLabel), ctaRightIcon ? /*#__PURE__*/React.createElement("span", {
    className: "iconRight",
    dangerouslySetInnerHTML: {
      __html: ctaRightIcon.svg.jss.value
    }
  }) : null)), !isDetSite && readAllLabel && readAllLinkHref && readAllLinkHref !== '/' && /*#__PURE__*/React.createElement(RouterLink, {
    className: "btn btn-link accordion__readmore",
    href: readAllLinkHref,
    onClick: () => tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['readAllAnalytics'], params)),
    dataCy: "accordion-faq-read-more",
    title: readAllLabel
  }, ctaLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: ctaLeftIcon.svg.jss.value
    }
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "btn-link__text"
  }, readAllLabel), ctaRightIcon ? /*#__PURE__*/React.createElement("span", {
    className: "iconRight",
    dangerouslySetInnerHTML: {
      __html: ctaRightIcon.svg.jss.value
    }
  }) : null)))));
};
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    isDetSite,
    anchorUrlParameter
  } = _ref2;
  const commonStyles = isDetSite ? "c1bjgzsn" : "ctqohm6";
  const ltrStyles = isDetSite ? "lscf0lu" : "lxmd3nf";
  const rtlStyles = isDetSite ? "rjc17rw" : "r88pj0q";
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    anchorUrlParameter: anchorUrlParameter,
    className: ` ${commonStyles} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
export default AccordionSteps;